/**
 * InformantFeedbackButton
 *
 * A self-contained button + dialog for viewing written feedback that an
 * informant left after completing their questionnaires. Extracted from
 * InformantHistoryPanel so the CYP ADHD respondent cards can render the
 * icon in the card header (top-right) rather than inside the History
 * section, while the Adult Autism pathway keeps it in its existing position
 * inside the panel header.
 *
 * Renders nothing when the informant has no written feedback, so callers
 * never need to guard.
 */
import React, { useState, useMemo } from 'react';
import { ChatAltIcon } from '@heroicons/react/solid';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { formatDateTime } from '../../../../utils/timestampUtils';

/**
 * Build the array of feedback entries from an informant's view data.
 *
 * Reads `informantViewData.formFeedback` — the feedback doc snapshot that
 * the all-forms-completed modal writes. Returns an empty array when there
 * is no written text, so the button component can render nothing.
 *
 * @param {Object} informantViewData - Enriched informant view (profile +
 *   formData + formInstances). Must include `.formFeedback` if feedback
 *   exists.
 * @param {string} informantId - The informant's UID, used as the entry key.
 * @returns {Array<{id,role,name,prompt,feedback,submittedAtLabel}>}
 */
export function buildInformantFeedbackEntries(informantViewData, informantId) {
  const entries = [];

  try {
    const ff = informantViewData?.formFeedback;
    const feedbackText = typeof ff?.feedback === 'string' ? ff.feedback.trim() : '';

    if (feedbackText) {
      const storedQuestion =
        typeof ff?.question === 'string' && ff.question.trim()
          ? ff.question.trim()
          : null;

      const name = informantViewData?.displayName
        || [informantViewData?.firstName, informantViewData?.lastName].filter(Boolean).join(' ')
        || informantViewData?.email
        || 'Informant';

      entries.push({
        id: informantId || 'informant',
        role: 'Informant',
        name,
        prompt: storedQuestion,
        feedback: feedbackText,
        submittedAtLabel: formatDateTime(ff.submittedAt, 'Unknown time'),
      });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('Failed to aggregate informant feedback:', e);
  }

  return entries;
}

/**
 * Feedback icon button + dialog. Renders nothing when there is no feedback.
 *
 * @param {Object} props
 * @param {Object} props.informantViewData - Enriched informant view object.
 * @param {string} props.informantId       - The informant's UID.
 * @param {string} props.contactNoun       - What to call this person in copy
 *   (e.g. 'informant', 'parent/carer', 'school representative').
 */
export default function InformantFeedbackButton({
  informantViewData,
  informantId,
  contactNoun = 'informant',
}) {
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);

  const feedbackEntries = useMemo(
    () => buildInformantFeedbackEntries(informantViewData, informantId),
    [informantViewData, informantId],
  );

  if (feedbackEntries.length === 0) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsFeedbackDialogOpen(true)}
        className="ml-2 mr-2 inline-flex items-center justify-center rounded-full p-1.5 text-primary hover:bg-violet-100 hover:text-violet-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
        aria-label={`View ${contactNoun} questionnaire feedback`}
      >
        <ChatAltIcon className="h-5 w-5" />
      </button>

      <AlertDialog.Root
        open={isFeedbackDialogOpen}
        onOpenChange={(open) => !open && setIsFeedbackDialogOpen(false)}
      >
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
          <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[95vw] max-w-xl max-h-[80vh] overflow-y-auto rounded-lg bg-white p-5 shadow-xl">
            <AlertDialog.Title className="text-lg font-semibold text-gray-900 mb-1">
              {/* Adult keeps its historical byte-exact title; CYP nouns get
                  sentence case per the design rules. */}
              {contactNoun === 'informant'
                ? 'Informant Questionnaire Feedback'
                : `${contactNoun.charAt(0).toUpperCase()}${contactNoun.slice(1)} questionnaire feedback`}
            </AlertDialog.Title>
            <AlertDialog.Description className="text-sm text-gray-600 mb-4">
              {`Written comments left by the ${contactNoun} after completing their questionnaires.`}
            </AlertDialog.Description>

            <div className="space-y-4">
              {feedbackEntries.map((entry) => (
                <div
                  key={entry.id}
                  className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-left"
                >
                  <div className="flex justify-end mb-1.5">
                    <span className="text-xs text-gray-500">
                      {entry.submittedAtLabel}
                    </span>
                  </div>

                  {entry.prompt && (
                    <p className="mb-1.5 text-xs font-medium text-gray-700">
                      {entry.prompt}
                    </p>
                  )}

                  <div className="rounded-md bg-white p-2 text-sm text-gray-800 border border-gray-200">
                    {entry.feedback}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex justify-end">
              <AlertDialog.Cancel asChild>
                <button
                  type="button"
                  onClick={() => setIsFeedbackDialogOpen(false)}
                  className="inline-flex items-center rounded-md bg-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-violet-500"
                >
                  Close
                </button>
              </AlertDialog.Cancel>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </>
  );
}
